// Shared builder for portal documents saved to Actionstep.
//
// Every tool that saves a document needs the same thing: an A4 sheet that looks like the
// one the tool prints. The print stylesheet is the reference - Georgia serif at 11pt, 12mm
// margins, a centred header ruled in the brand colour, and small uppercase sans-serif
// section headings ruled the same way.
//
// This exists so that is written once. Seven tools each laying out their own PDF would
// drift apart, and a firm would end up with documents on one matter that plainly came from
// different systems.
//
// Deliberately real text rather than a screenshot of the page. html2canvas would match the
// print view more closely but produces a raster image: unsearchable, uncopyable, and poor
// when reprinted - the wrong trade for a document a firm may have to produce or quote from
// years later.

import { jsPDF } from "jspdf";

const PAGE_WIDTH = 210;
const PAGE_HEIGHT = 297;
const MARGIN = 12;
const CONTENT_WIDTH = PAGE_WIDTH - MARGIN * 2;

function toRgb(hex) {
  const value = String(hex || "#1a1a1a").replace("#", "");
  const full = value.length === 3 ? value.split("").map((c) => c + c).join("") : value;
  return [0, 2, 4].map((i) => parseInt(full.slice(i, i + 2), 16) || 0);
}

/**
 * Creates a document builder. Callers add sections and call `finish()`.
 *
 * @param {object} options
 * @param {string} options.title      Document title, centred in the header.
 * @param {string} [options.subtitle] Small uppercase line beneath it.
 * @param {string} [options.brandColor] Hex colour for rules and headings.
 */
export function createPortalPdf({ title, subtitle = "COMPLETE LAW SUPPORT", brandColor = "#1a1a1a" }) {
  const pdf = new jsPDF("p", "mm", "a4");
  const [br, bg, bb] = toRgb(brandColor);
  let y = MARGIN + 6;

  const pageBreak = (needed) => {
    if (y + needed > PAGE_HEIGHT - MARGIN) {
      pdf.addPage();
      y = MARGIN + 6;
    }
  };

  const api = {
    /** Body text. Wraps, paginates, and returns the builder for chaining. */
    body(text, options = {}) {
      pdf.setFont("times", options.style || "normal");
      pdf.setFontSize(options.size || 11);
      pdf.setTextColor(26, 26, 26);
      const width = options.width || CONTENT_WIDTH - (options.indent || 0);
      const lines = pdf.splitTextToSize(String(text ?? ""), width);
      const lineHeight = options.lineHeight || 5;
      pageBreak(lines.length * lineHeight);
      pdf.text(lines, MARGIN + (options.indent || 0), y);
      y += lines.length * lineHeight + (options.after ?? 1.5);
      return api;
    },

    /** A ruled section heading, matching the print stylesheet. */
    heading(label) {
      pageBreak(12);
      y += 3;
      pdf.setFont("helvetica", "bold");
      pdf.setFontSize(8);
      pdf.setTextColor(br, bg, bb);
      pdf.text(String(label).toUpperCase(), MARGIN, y, { charSpace: 0.5 });
      y += 1.5;
      pdf.setDrawColor(br, bg, bb);
      pdf.setLineWidth(0.3);
      pdf.line(MARGIN, y, MARGIN + CONTENT_WIDTH, y);
      y += 4;
      return api;
    },

    /** A label/value row. Empty values read "N/A" rather than leaving a gap. */
    field(label, value) {
      pageBreak(6);
      pdf.setFont("helvetica", "bold");
      pdf.setFontSize(8);
      pdf.setTextColor(110, 110, 110);
      pdf.text(String(label).toUpperCase(), MARGIN, y, { charSpace: 0.3 });
      pdf.setFont("times", "normal");
      pdf.setFontSize(11);
      pdf.setTextColor(26, 26, 26);
      const text = String(value ?? "").trim() || "N/A";
      pdf.text(pdf.splitTextToSize(text, CONTENT_WIDTH - 42), MARGIN + 42, y);
      y += 6;
      return api;
    },

    /** A bulleted item. */
    bullet(text) {
      return api.body(`•  ${text}`, { indent: 2, after: 1 });
    },

    /** Several label/value rows at once. Falsy entries are skipped. */
    fields(rows) {
      rows.filter(Boolean).forEach(([label, value]) => api.field(label, value));
      return api;
    },

    /** The finished jsPDF document. */
    finish() {
      return pdf;
    },

    /** The document as a File, ready to upload. */
    toFile(fileName) {
      return new File([pdf.output("blob")], fileName, { type: "application/pdf" });
    },
  };

  // Header, drawn immediately so callers start with content.
  pdf.setFont("times", "bold");
  pdf.setFontSize(15);
  pdf.setTextColor(26, 26, 26);
  pdf.text(String(title || "Document"), PAGE_WIDTH / 2, y, { align: "center" });
  y += 5;
  if (subtitle) {
    pdf.setFont("helvetica", "normal");
    pdf.setFontSize(7.5);
    pdf.setTextColor(136, 136, 136);
    pdf.text(String(subtitle).toUpperCase(), PAGE_WIDTH / 2, y, { align: "center", charSpace: 0.6 });
    y += 3;
  }
  pdf.setDrawColor(br, bg, bb);
  pdf.setLineWidth(0.7);
  pdf.line(MARGIN, y, MARGIN + CONTENT_WIDTH, y);
  y += 7;

  return api;
}

/** A safe, descriptive file name: client and date, so a matter folder stays readable. */
export function portalDocumentName(prefix, clientName, date) {
  const client = String(clientName || "client")
    .replace(/[^A-Za-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "") || "client";
  const when = date || new Date().toISOString().slice(0, 10);
  return `${prefix}-${client}-${when}.pdf`;
}
